import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';
import Preload from './components/Preload';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import './styles/global.scss';
import './styles/mobile.scss';

// Lazy load pages for code splitting
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Ebooks = lazy(() => import('./pages/Ebooks'));
const Videos = lazy(() => import('./pages/Videos'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const PaymentSecurity = lazy(() => import('./pages/PaymentSecurity'));
const Plans = lazy(() => import('./pages/Plans'));
const Payment = lazy(() => import('./pages/Payment'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const LessonViewer = lazy(() => import('./pages/LessonViewer'));
const Login = lazy(() => import('./pages/Login'));
const SsoCallback = lazy(() => import('./pages/SsoCallback'));
const ClaimAccount = lazy(() => import('./pages/ClaimAccount'));
// Profile page removed as it's no longer needed
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Preload />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={
              <Suspense fallback={<LoadingSpinner />}>
                <Home />
              </Suspense>
            } />
            <Route path="about" element={
              <Suspense fallback={<LoadingSpinner />}>
                <About />
              </Suspense>
            } />
            <Route path="ebooks" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Ebooks />
              </Suspense>
            } />
            <Route path="videos" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Videos />
              </Suspense>
            } />
            <Route path="contact" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Contact />
              </Suspense>
            } />
            <Route path="faq" element={
              <Suspense fallback={<LoadingSpinner />}>
                <FAQ />
              </Suspense>
            } />
            <Route path="privacy-policy" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivacyPolicy />
              </Suspense>
            } />
            <Route path="terms-conditions" element={
              <Suspense fallback={<LoadingSpinner />}>
                <TermsConditions />
              </Suspense>
            } />
            <Route path="refund-policy" element={
              <Suspense fallback={<LoadingSpinner />}>
                <RefundPolicy />
              </Suspense>
            } />
            <Route path="payment-security" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PaymentSecurity />
              </Suspense>
            } />
            <Route path="plans" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Plans />
              </Suspense>
            } />
            <Route path="payment" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Payment />
              </Suspense>
            } />
            <Route path="payment-success" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PaymentSuccess />
              </Suspense>
            } />
            <Route path="courses/:slug" element={
              <Suspense fallback={<LoadingSpinner />}>
                <CourseDetail />
              </Suspense>
            } />
            <Route path="learn/:courseId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProtectedRoute>
                  <LessonViewer />
                </ProtectedRoute>
              </Suspense>
            } />
            <Route path="login" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Login />
              </Suspense>
            } />
            <Route path="sso-callback" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SsoCallback />
              </Suspense>
            } />
            <Route path="claim-account" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ClaimAccount />
              </Suspense>
            } />
            {/* Profile route removed as it's no longer needed */}
            <Route path="*" element={
              <Suspense fallback={<LoadingSpinner />}>
                <NotFound />
              </Suspense>
            } />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
